import colorFormatter from 'utils/colorFormatter'

export const DEFAULT_APPOINTMENT_PREFERENCES = {
    confirmedColor: '#0ea5e9',
    billedColor: '#22c55e',
    reminderColor: '#f59e0b',
    noAnswerColor: '#64748b',
    rescheduleColor: '#2563eb',
    cancelledColor: '#ef4444',
}

export const DEFAULT_APPOINTMENT_COLOR = '#64748b'

export const APPOINTMENT_OPERATIONAL_STATUSES = [
    {
        value: 'a_rappeler',
        label: 'À rappeler',
        description: 'Le cabinet doit rappeler la patiente.',
        preferenceKey: 'reminderColor',
    },
    {
        value: 'ne_repond_pas',
        label: 'Ne répond pas',
        description: "La patiente n'a pas répondu.",
        preferenceKey: 'noAnswerColor',
    },
    {
        value: 'a_reprogrammer',
        label: 'À reprogrammer',
        description: 'Un nouveau créneau doit être convenu.',
        preferenceKey: 'rescheduleColor',
    },
    {
        value: 'annule',
        label: 'Annulé',
        description: "Le rendez-vous n'aura pas lieu.",
        preferenceKey: 'cancelledColor',
    },
]

const INACTIVE_OPERATIONAL_STATUSES = new Set(['a_reprogrammer', 'annule'])

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/

export const getSafeColor = (color, fallback = DEFAULT_APPOINTMENT_COLOR) =>
    HEX_COLOR_REGEX.test(color || '') ? color : fallback

export const getAppointmentOperationalStatusMeta = (
    status,
    preferences = DEFAULT_APPOINTMENT_PREFERENCES
) => {
    const definition = APPOINTMENT_OPERATIONAL_STATUSES.find(
        (item) => item.value === status
    )

    if (!definition) return null

    return {
        ...definition,
        color: getSafeColor(
            preferences?.[definition.preferenceKey],
            DEFAULT_APPOINTMENT_PREFERENCES[definition.preferenceKey]
        ),
    }
}

export const getAppointmentOperationalStatus = (appointment) => {
    if (getAppointmentOperationalStatusMeta(appointment?.appointmentStatus)) {
        return appointment.appointmentStatus
    }

    return appointment?.declinedStatus?.isDeclined ? 'annule' : ''
}

export const canConfirmAppointment = (appointment) =>
    Boolean(appointment) &&
    !appointment.isBilled &&
    !appointment.deletedStatus?.isDeleted &&
    (!appointment.isConfirmed ||
        Boolean(getAppointmentOperationalStatus(appointment)))

export const isAppointmentOperationallyInactive = (appointment) =>
    Boolean(appointment?.declinedStatus?.isDeclined) ||
    INACTIVE_OPERATIONAL_STATUSES.has(appointment?.appointmentStatus)

export const getAppointmentDisplayColor = (
    appointment,
    preferences = DEFAULT_APPOINTMENT_PREFERENCES
) => {
    const operationalStatus = getAppointmentOperationalStatus(appointment)
    const operationalStatusMeta = getAppointmentOperationalStatusMeta(
        operationalStatus,
        preferences
    )

    if (INACTIVE_OPERATIONAL_STATUSES.has(operationalStatus)) {
        return operationalStatusMeta.color
    }

    if (appointment?.isBilled) {
        return getSafeColor(
            preferences?.billedColor,
            DEFAULT_APPOINTMENT_PREFERENCES.billedColor
        )
    }

    if (operationalStatusMeta) {
        return operationalStatusMeta.color
    }

    if (appointment?.isConfirmed) {
        return getSafeColor(
            preferences?.confirmedColor,
            DEFAULT_APPOINTMENT_PREFERENCES.confirmedColor
        )
    }

    return getSafeColor(appointment?.eventColor)
}

export const getAppointmentStatusMeta = (
    appointment,
    preferences = DEFAULT_APPOINTMENT_PREFERENCES
) => {
    const operationalStatus = getAppointmentOperationalStatus(appointment)
    const operationalStatusMeta = getAppointmentOperationalStatusMeta(
        operationalStatus,
        preferences
    )

    if (INACTIVE_OPERATIONAL_STATUSES.has(operationalStatus)) {
        return operationalStatusMeta
    }

    if (appointment?.isBilled) {
        return {
            label: 'Encaissé',
            color: getSafeColor(
                preferences?.billedColor,
                DEFAULT_APPOINTMENT_PREFERENCES.billedColor
            ),
        }
    }

    if (operationalStatusMeta) {
        return operationalStatusMeta
    }

    if (appointment?.isConfirmed) {
        return {
            label: 'Confirmé',
            color: getSafeColor(
                preferences?.confirmedColor,
                DEFAULT_APPOINTMENT_PREFERENCES.confirmedColor
            ),
        }
    }

    return {
        label: 'Non confirmé',
        color: DEFAULT_APPOINTMENT_COLOR,
    }
}

export const getStatusBadgeStyle = (color) => {
    const safeColor = getSafeColor(color)
    const { darkColor, lightColor } = colorFormatter(safeColor)

    return {
        backgroundColor: lightColor,
        border: `1px solid ${darkColor}`,
        color: darkColor,
    }
}

export const getStatusRowBackgroundColor = (color) => {
    const safeColor = getSafeColor(color)
    const numericColor = Number.parseInt(safeColor.slice(1), 16)
    const red = (numericColor >> 16) & 255
    const green = (numericColor >> 8) & 255
    const blue = numericColor & 255

    return `rgba(${red}, ${green}, ${blue}, 0.08)`
}

export const getStatusRowStyle = (appointment, preferences) => {
    const operationalStatus = getAppointmentOperationalStatus(appointment)

    if (
        !operationalStatus &&
        !appointment?.isBilled &&
        !appointment?.isConfirmed
    ) {
        return undefined
    }

    return {
        backgroundColor: getStatusRowBackgroundColor(
            getAppointmentDisplayColor(appointment, preferences)
        ),
    }
}
